// components/operations/taxi/TaxiManifestDashboard.jsx

import React, {
    startTransition,
    useCallback,
    useMemo,
    useState,
} from "react";

import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";

import {
    Assignment as AssignmentIcon,
    LocalTaxi as LocalTaxiIcon,
    Person as PersonIcon,
    DirectionsCar as DirectionsCarIcon,
} from "@mui/icons-material";

import { useManifestsPaged } from "../../../hooks/useManifest";
import TaxiManifestOperationDialog from "./TaxiManifestOperationDialog";

const DEFAULT_LIMIT = 50;

const TAXI_STATES = [
    "CREADO",
    "PLANIFICADO",
    "EN_PROCESO",
    "COMPLETADO",
];

const MANIFEST_STATE_COLOR = {
    CREADO: "default",
    PLANIFICADO: "warning",
    EN_PROCESO: "success",
    COMPLETADO: "info",
    CANCELADO: "error",
};

const formatCode = (id, prefix) =>
    id
        ? `${prefix}-${String(id).padStart(6, "0")}`
        : "—";

const TaxiManifestDashboard = () => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] =
        useState(DEFAULT_LIMIT);

    const [selectedManifest, setSelectedManifest] = useState(null);

    const [alert, setAlert] = useState({
        open: false,
        severity: "success",
        message: "",
    });

    const offset = page * rowsPerPage;

    const {
        data: response,
        isLoading,
        isFetching,
        error,
    } = useManifestsPaged({
        tipoServicioCodigo: "TAXI",
        estados: TAXI_STATES,
        limit: rowsPerPage,
        offset,
        enabled: true,
    });

    const manifests = response?.data ?? [];

    const disableNext = useMemo(() => {
        return manifests.length < rowsPerPage;
    }, [manifests.length, rowsPerPage]);

    const handlePageChange = useCallback((_, nextPage) => {
        startTransition(() => {
            setPage(nextPage);
        });
    }, []);

    const handleRowsPerPageChange = useCallback((event) => {
        const nextLimit = Number(event.target.value);

        startTransition(() => {
            setRowsPerPage(nextLimit);
            setPage(0);
        });
    }, []);

    const showAlert = useCallback(
        (message, severity = "success") => {
            setAlert({
                open: true,
                severity,
                message,
            });
        },
        []
    );

    const handleOperationSuccess = useCallback(
        (_, message) => {
            showAlert(message);
            setSelectedManifest(null);
        },
        [showAlert]
    );

    if (isLoading) {
        return (
            <Box
                minHeight={250}
                display="flex"
                justifyContent="center"
                alignItems="center"
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error">
                {error?.response?.data?.error ||
                    error?.message ||
                    "No se pudieron cargar los manifiestos taxi."}
            </Alert>
        );
    }

    return (
        <Paper
            sx={{
                p: 2,
                position: "relative",
            }}
        >
            {alert.open && (
                <Alert
                    severity={alert.severity}
                    onClose={() =>
                        setAlert((previous) => ({
                            ...previous,
                            open: false,
                        }))
                    }
                    sx={{ mb: 2 }}
                >
                    {alert.message}
                </Alert>
            )}

            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                mb={3}
            >
                <Box>
                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        display="flex"
                        alignItems="center"
                        gap={1}
                    >
                        <LocalTaxiIcon color="warning" />
                        Operación de manifiestos taxi
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        Asignación rápida de equipos y liberación operativa.
                    </Typography>
                </Box>

                {isFetching && <CircularProgress size={24} />}
            </Box>

            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: 2 }}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <strong>Manifiesto</strong>
                            </TableCell>

                            <TableCell>
                                <strong>Orden</strong>
                            </TableCell>

                            <TableCell>
                                <strong>Fecha</strong>
                            </TableCell>

                            <TableCell>
                                <strong>Origen</strong>
                            </TableCell>

                            <TableCell>
                                <strong>Destino</strong>
                            </TableCell>

                            <TableCell>
                                <strong>Conductor</strong>
                            </TableCell>

                            <TableCell>
                                <strong>Vehículo</strong>
                            </TableCell>

                            <TableCell>
                                <strong>Estado</strong>
                            </TableCell>

                            <TableCell align="center">
                                <strong>Operación</strong>
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {manifests.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                                    <Typography color="text.secondary">
                                        No existen manifiestos de taxi pendientes.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            manifests.map((manifest) => {
                                const employees = Array.isArray(
                                    manifest.empleados
                                )
                                    ? manifest.empleados
                                    : [];

                                const driver = employees[0];

                                return (
                                    <TableRow key={manifest.manid} hover>
                                        <TableCell>
                                            <Typography fontWeight="bold" color="primary">
                                                {formatCode(manifest.manid, "MN")}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            {formatCode(
                                                manifest.ordsercabid,
                                                "OS"
                                            )}
                                        </TableCell>

                                        <TableCell>
                                            {manifest.manfec || "—"}
                                        </TableCell>

                                        <TableCell>
                                            {manifest.manori || "—"}
                                        </TableCell>

                                        <TableCell>
                                            {manifest.mandes || "—"}
                                        </TableCell>

                                        {/*<TableCell>
                                            {driver
                                                ? `${driver.perNom || ""} ${driver.perApe || ""
                                                    }`.trim()
                                                : (
                                                    <Chip
                                                        size="small"
                                                        label="Sin conductor"
                                                        color="warning"
                                                        variant="outlined"
                                                    />
                                                )}
                                        </TableCell>

                                        <TableCell>
                                            {manifest.vehiculo?.vehmat ||
                                                manifest.vehmat || (
                                                    <Chip
                                                        size="small"
                                                        label="Sin vehículo"
                                                        color="warning"
                                                        variant="outlined"
                                                    />
                                                )}
                                        </TableCell>*/}

                                        <TableCell>
                                            {driver ? (
                                                <Chip
                                                    size="small"
                                                    icon={<PersonIcon />}
                                                    label={`${driver.perNom || ''} ${driver.perApe || ''}`.trim()}
                                                    color="primary"
                                                    variant="filled"
                                                    sx={{
                                                        fontWeight: 500,
                                                        borderRadius: '16px',
                                                        backgroundColor: '#e3f2fd',   // azul muy claro
                                                        color: '#0d47a1',             // azul oscuro
                                                        '& .MuiChip-icon': { color: '#0d47a1' },
                                                    }}
                                                />
                                            ) : (
                                                <Chip
                                                    size="small"
                                                    label="Sin conductor"
                                                    color="warning"
                                                    variant="outlined"
                                                />
                                            )}
                                        </TableCell>

                                        <TableCell>
                                            {manifest.vehiculo?.vehmat || manifest.vehmat ? (
                                                <Chip
                                                    size="small"
                                                    icon={<DirectionsCarIcon />}
                                                    label={manifest.vehiculo?.vehmat || manifest.vehmat}
                                                    color="info"
                                                    variant="filled"
                                                    sx={{
                                                        fontWeight: 500,
                                                        borderRadius: '16px',
                                                        backgroundColor: '#e8f5e9',   // verde muy claro
                                                        color: '#1b5e20',             // verde oscuro
                                                        '& .MuiChip-icon': { color: '#1b5e20' },
                                                    }}
                                                />
                                            ) : (
                                                <Chip
                                                    size="small"
                                                    label="Sin vehículo"
                                                    color="warning"
                                                    variant="outlined"
                                                />
                                            )}
                                        </TableCell>

                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={manifest.manest}
                                                color={
                                                    MANIFEST_STATE_COLOR[
                                                    manifest.manest
                                                    ] || "default"
                                                }
                                                sx={{ fontWeight: "bold" }}
                                            />
                                        </TableCell>

                                        <TableCell align="center">
                                            <Tooltip title="Asignar equipo y gestionar">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() =>
                                                        setSelectedManifest(manifest)
                                                    }
                                                >
                                                    <AssignmentIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={-1}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[25, 50, 100]}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                nextIconButtonProps={{
                    disabled: disableNext,
                }}
            />

            <TaxiManifestOperationDialog
                open={Boolean(selectedManifest)}
                manifest={selectedManifest}
                onClose={() => setSelectedManifest(null)}
                onSuccess={handleOperationSuccess}
            />
        </Paper>
    );
};

export default TaxiManifestDashboard;